import { z } from 'zod'
import { es } from '../../content/es'

/** Clave de un mensaje de es.validacion: el esquema guarda la clave y la pantalla muestra el texto del idioma vigente. */
export type ClaveValidacion = keyof typeof es.validacion

const texto = (min: number, mensaje: ClaveValidacion) => z.string().trim().min(min, mensaje)
const opcional = z.string().trim().optional().transform((v) => (v ? v : undefined))

export const esquemaFiscal = z.object({
  razon_social: texto(3, 'razonSocial'),
  rfc: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/, 'rfc'),
  tipo: z.enum(['farmacia', 'clinica', 'consultorio', 'distribuidor'], { message: 'tipoEmpresa' }),
  codigo_postal_fiscal: z.string().trim().regex(/^[0-9]{5}$/, 'codigoPostal'),
  email_facturacion: z.string().trim().email('correo'),
  telefono: opcional.refine((v) => !v || /^[0-9+()\s-]{8,20}$/.test(v), 'telefono'),
})

export const esquemaSanitario = z
  .object({
    aviso_funcionamiento: opcional,
    licencia_sanitaria: opcional,
    responsable_sanitario: texto(3, 'responsable'),
    cedula_responsable: z.string().trim().regex(/^[0-9]{7,8}$/, 'cedula'),
  })
  .refine((v) => v.aviso_funcionamiento || v.licencia_sanitaria, {
    message: 'faltaAviso',
    path: ['aviso_funcionamiento'],
  })

export const esquemaSucursal = z.object({
  alias: texto(2, 'alias'),
  calle: texto(2, 'calle'),
  numero_exterior: texto(1, 'numeroExterior'),
  numero_interior: opcional,
  colonia: texto(2, 'colonia'),
  municipio: texto(2, 'municipio'),
  estado_mx: texto(2, 'estado'),
  codigo_postal: z.string().trim().regex(/^[0-9]{5}$/, 'codigoPostal'),
  contacto_nombre: texto(3, 'contacto'),
  contacto_telefono: z.string().trim().regex(/^[0-9+()\s-]{8,20}$/, 'telefono'),
  horario: opcional,
  referencias: opcional,
})

export type DatosFiscales = z.infer<typeof esquemaFiscal>
export type DatosSanitarios = z.infer<typeof esquemaSanitario>
export type DatosSucursal = z.infer<typeof esquemaSucursal>

/** Convierte los errores de zod en un mensaje por campo (el primero de cada uno). */
export function erroresPorCampo(error: z.ZodError): Record<string, string> {
  const salida: Record<string, string> = {}
  for (const i of error.issues) {
    const clave = String(i.path[0] ?? '')
    if (clave && !salida[clave]) salida[clave] = (es.validacion as Record<string, string>)[i.message] ?? i.message
  }
  return salida
}
